import React, { useState } from 'react';
import axios from '../config/axiosConfig';
import './RegisterModal.css';
import {
  FaUser, FaEnvelope, FaPhone, FaBuilding, FaTimes
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext';

const RegisterModal = ({ onClose, onRegister, onSwitchToLogin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    user_type: 'employer',
    company_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic validation
    if (!formData.full_name.trim()) {
      setError(t('registerErrorFullName') || 'Please enter your full name');
      setLoading(false);
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError(t('registerErrorEmailInvalid') || 'Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        'https://hsn.urugowoc.org.rw/backend/api/register',
        {
          full_name: formData.full_name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone || null,
          user_type: 'employer',
          company_name: formData.company_name || null
        }
      );

      // ========== AUTO-LOGIN AFTER REGISTRATION ==========
      if (response.data.success && response.data.token) {
        // Store token and user
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        // Notify parent (App.js) about the login
        if (onRegister) {
          onRegister(response.data.user, response.data.token);
        }

        // Close modal
        onClose();

        // Navigate to employer dashboard
        setTimeout(() => {
          navigate('/employer-dashboard', { replace: true });
        }, 100);
      } else {
        throw new Error(response.data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const msg =
        error.response?.data?.error ||
        t('registerErrorGeneric') ||
        'Registration failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-modal-overlay">
      <div className="register-modal-content">
        <button className="register-close-btn" onClick={onClose} title={t('close') || 'Close'}>
          <FaTimes />
        </button>

        <div className="register-modal-header">
          <h2>{t('registerCreateAccount') || 'Create Your Account'}</h2>
          <p>{t('registerDescriptionNoPassword') || 'Just enter your details — no password required'}</p>
        </div>

        <div className="register-modal-body">
          {error && (
            <div className="register-alert register-alert-error">
              {error}
            </div>
          )}

          <form className="register-form" onSubmit={handleSubmit}>
            {/* Full Name */}
            <div className="register-form-group">
              <label>{t('fullNameLabel') || 'Full Name'} *</label>
              <div className="register-input-wrapper">
                <FaUser className="register-input-icon" />
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder={t('fullNamePlaceholder') || 'Enter your full name'}
                  required
                  disabled={loading}
                  className="register-input"
                />
              </div>
            </div>

            {/* Email */}
            <div className="register-form-group">
              <label>{t('emailLabel') || 'Email Address'} *</label>
              <div className="register-input-wrapper">
                <FaEnvelope className="register-input-icon" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('emailPlaceholder') || 'Enter your email'}
                  required
                  disabled={loading}
                  className="register-input"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="register-form-group">
              <label>{t('phoneLabel') || 'Phone Number'}</label>
              <div className="register-input-wrapper">
                <FaPhone className="register-input-icon" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('phonePlaceholder') || 'Enter your phone number'}
                  disabled={loading}
                  className="register-input"
                />
              </div>
            </div>

            {/* Account Type */}
            <div className="register-user-type">
              <h3>{t('userTypeTitle') || 'Account Type'}</h3>
              <div className="register-user-options">
                <div
                  className={`register-user-option ${formData.user_type === 'employer' ? 'register-selected' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, user_type: 'employer' }))}
                >
                  <FaBuilding className="register-user-icon" />
                  <h4>{t('userTypeEmployer') || 'Employer'}</h4>
                  <p>{t('userTypeEmployerDesc') || 'Hire talent and manage jobs'}</p>
                </div>
              </div>
            </div>

            {/* Company Name (optional) */}
            {formData.user_type === 'employer' && (
              <div className="register-form-group">
                <label>{t('companyNameLabel') || 'Company Name'}</label>
                <div className="register-input-wrapper">
                  <FaBuilding className="register-input-icon" />
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder={t('companyNamePlaceholder') || 'Enter your company name (optional)'}
                    disabled={loading}
                    className="register-input"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              className="register-btn register-btn-primary"
              disabled={loading}
            >
              {loading
                ? (t('creatingAccount') || 'Creating account...')
                : (t('signUp') || 'Sign Up')}
            </button>
          </form>
        </div>

        <div className="register-modal-footer">
          <div className="register-login-prompt">
            {t('alreadyHaveAccount') || 'Already have an account?'}{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                onSwitchToLogin();
              }}
            >
              {t('signIn') || 'Sign In'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterModal;